// BenevoleDashboard.jsx — Interface Bénévole : mes missions + missions ouvertes.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { HandHeart, MapPin, CalendarDays, Users, Loader2, Check, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { RoleShell } from './RoleShell';

export default function BenevoleDashboard() {
  const { user } = useAuth();
  const accent = user?.couleur_principale || '#9333EA';
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get('/benevole/missions').then(({ data }) => setData(data)).catch(() => setData({ mes_missions: [], disponibles: [] }));
  useEffect(() => { load(); }, []);

  const inscrire = async (mid) => {
    setBusy(mid);
    try { const { data } = await api.post(`/benevole/missions/${mid}/inscription`); toast.success(data.message || 'Inscrit !'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    finally { setBusy(null); }
  };
  const desinscrire = async (mid) => {
    setBusy(mid);
    try { const { data } = await api.delete(`/benevole/missions/${mid}/inscription`); toast.success(data.message || 'Retiré'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    finally { setBusy(null); }
  };

  return (
    <RoleShell roleLabel="Bénévole" title="Mes missions" subtitle="Donnez un coup de main : inscrivez-vous aux missions ouvertes." testid="benevole-dashboard">
      {data === null ? (
        <div className="grid gap-4 sm:grid-cols-2">{[0, 1].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-[#F1F5F9] animate-pulse" />)}</div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="font-display font-black text-xl text-[#0F172A] mb-3">Je participe ({data.mes_missions.length})</h2>
            {data.mes_missions.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">Vous n'êtes inscrit·e à aucune mission pour le moment.</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="benevole-mes-missions">
                {data.mes_missions.map((m) => (
                  <MissionCard key={m.id} m={m} accent={accent} mine busy={busy === m.id} onAction={() => desinscrire(m.id)} />
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-display font-black text-xl text-[#0F172A] mb-3">Missions ouvertes ({data.disponibles.length})</h2>
            {data.disponibles.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">Aucune mission ouverte actuellement. Merci pour votre engagement !</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="benevole-disponibles">
                {data.disponibles.map((m) => (
                  <MissionCard key={m.id} m={m} accent={accent} busy={busy === m.id} onAction={() => inscrire(m.id)} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </RoleShell>
  );
}

function MissionCard({ m, accent, mine, busy, onAction }) {
  return (
    <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-5 flex flex-col" data-testid={`mission-card-${m.id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="w-9 h-9 rounded-xl grid place-items-center text-white shrink-0" style={{ background: accent }}><HandHeart className="w-4 h-4" /></div>
        <h3 className="font-display font-black text-[#0F172A] leading-tight">{m.titre}</h3>
      </div>
      {m.description && <p className="text-sm text-[#475569] mb-3">{m.description}</p>}
      <div className="space-y-1.5 text-sm text-[#475569] mb-4">
        {m.date && <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4" style={{ color: accent }} /> {m.date}</div>}
        {m.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: accent }} /> {m.lieu}</div>}
        <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: accent }} /> {m.nb_inscrits}/{m.besoins} bénévoles{!mine && m.places_restantes > 0 ? ` · ${m.places_restantes} place·s` : ''}</div>
      </div>
      <button
        onClick={onAction}
        disabled={busy}
        className="mt-auto w-full inline-flex items-center justify-center gap-2 text-sm font-bold rounded-xl py-2.5 transition-all disabled:opacity-60"
        style={mine ? { border: '2px solid #F1F5F9', color: '#EF4444' } : { background: accent, color: '#fff' }}
        data-testid={mine ? `mission-leave-${m.id}` : `mission-join-${m.id}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mine ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
        {mine ? 'Me retirer' : "Je m'inscris"}
      </button>
    </div>
  );
}
